import React, { useEffect, useEffectEvent, useMemo, useRef, useState } from "react";

import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";

import { Alert, PageBanner, Spinner, Text, useWizard } from "@performyard/py-components";

import EmployeeSelection from "../../../../components/EmployeeSelection";

import { useUsersWithoutCompensationData } from "../../../../api/hooks";
import { useAddUsersToSelection, useCreateUserSelection } from "../../../../api/hooks/defs/user-selection";

type EmployeeSelectionStepProps = {
	userSelectionId: string | null;
	onSelectionCreated: (id: string) => void;
	showSelectionError?: boolean;
};

const emptyExclusionList: string[] = [];

const EmployeeSelectionStep = ({
	userSelectionId,
	onSelectionCreated,
	showSelectionError,
}: EmployeeSelectionStepProps) => {
	const { setNextDisabled } = useWizard();

	const createSelection = useCreateUserSelection({
		onSuccess: (data) => onSelectionCreated(data.user_selection_id),
	});

	const { data: usersWithoutCompensationData, isLoading: isLoadingUsersWithoutCompData } =
		useUsersWithoutCompensationData();

	const isInitializing = !userSelectionId || isLoadingUsersWithoutCompData;
	const [isTableBusy, setIsTableBusy] = useState(false);

	useEffect(() => {
		setNextDisabled(isInitializing || isTableBusy);
		return () => setNextDisabled(false);
	}, [isInitializing, isTableBusy, setNextDisabled]);

	const excludeUserIds = useMemo(
		() => usersWithoutCompensationData?.uniqueExcludedUsers?.map((user) => user._id) ?? [],
		[usersWithoutCompensationData?.uniqueExcludedUsers]
	);

	const exclusionFingerprint = useMemo(() => {
		if (excludeUserIds.length === 0) return "";
		return [...excludeUserIds].sort().join("|");
	}, [excludeUserIds]);

	const [exclusionSelectionId, setExclusionSelectionId] = useState<string | null>(null);
	const syncedExclusionFingerprintRef = useRef<string | null>(null);
	const inFlightExclusionFingerprintRef = useRef<string | null>(null);

	const { mutate: createExclusionSelection } = useCreateUserSelection();
	const { mutate: addExcludedUsersToSelection } = useAddUsersToSelection();

	useEffect(() => {
		if (exclusionFingerprint === "") {
			setExclusionSelectionId(null);
			syncedExclusionFingerprintRef.current = null;
			inFlightExclusionFingerprintRef.current = null;
			return;
		}
		if (syncedExclusionFingerprintRef.current === exclusionFingerprint) {
			return;
		}
		if (inFlightExclusionFingerprintRef.current === exclusionFingerprint) {
			return;
		}
		inFlightExclusionFingerprintRef.current = exclusionFingerprint;

		createExclusionSelection(undefined, {
			onSuccess: (data) => {
				const selectionId = data.user_selection_id;
				addExcludedUsersToSelection(
					{ selectionObjectID: selectionId, users: excludeUserIds },
					{
						onSuccess: () => {
							setExclusionSelectionId(selectionId);
							syncedExclusionFingerprintRef.current = exclusionFingerprint;
						},
						onSettled: () => {
							if (inFlightExclusionFingerprintRef.current === exclusionFingerprint) {
								inFlightExclusionFingerprintRef.current = null;
							}
						},
					}
				);
			},
			onError: () => {
				if (inFlightExclusionFingerprintRef.current === exclusionFingerprint) {
					inFlightExclusionFingerprintRef.current = null;
				}
			},
		});
	}, [addExcludedUsersToSelection, createExclusionSelection, excludeUserIds, exclusionFingerprint]);

	const initializeSelection = useEffectEvent(() => {
		if (!userSelectionId) {
			createSelection.mutate();
		}
	});

	useEffect(() => {
		initializeSelection();
	}, []);

	if (!userSelectionId || isLoadingUsersWithoutCompData) {
		return (
			<div className="flex items-center justify-center py-12">
				<Spinner />
			</div>
		);
	}

	const countOfExcludedUserIds = usersWithoutCompensationData?.totalCount || 0;
	const showBanner = countOfExcludedUserIds > 0;
	const employeeSelectionHeight = showBanner ? "calc(100dvh - 336px)" : "calc(100dvh - 272px)";

	return (
		<div className="mx-auto flex max-w-[1096px] flex-col gap-4 px-4">
			<div className="mb-4 text-center">
				<Text size="sub-heading" weight="semi-bold">
					Create a Compensation Cycle
				</Text>
				<Text size="paragraph-2" weight="regular">
					Employee Selection
				</Text>
			</div>
			{showSelectionError && <Alert type="error" message="At least one employee must be selected to continue." />}
			{countOfExcludedUserIds > 0 && (
				<PageBanner
					color="primary"
					icon={faTriangleExclamation}
					mainTitle={`${countOfExcludedUserIds} employee${countOfExcludedUserIds === 1 ? " is" : "s are"} excluded from this cycle due to missing manager or salary data. See the list of excluded employees on the Compensation Administration page.`}
				/>
			)}
			<div className="min-h-0">
				<EmployeeSelection
					selectionObjectID={userSelectionId}
					customFieldsIdentifiers={["Group", "Hire Date", "Manager"]}
					exclusionList={exclusionSelectionId ? emptyExclusionList : excludeUserIds}
					usersToExcludeSelectionId={exclusionSelectionId}
					filters={{}}
					limit={100}
					paginationAlignment="end"
					onlySelected={false}
					height={employeeSelectionHeight}
					onMutatingChange={setIsTableBusy}
				/>
			</div>
		</div>
	);
};

export default EmployeeSelectionStep;
